<template>
    <div
        v-if="!settings.is_sourcing_mode"
        :class="isLarge ? 'at1024:p-8 gap-3' : 'gap-4'"
        class="p-4 bg-white border border-gray-325 flex flex-col rounded-md at1024:border-gray-250"
    >
        <div class="flex gap-2">
            <p :class="isLarge ? 'text-xs at1024:text-sm': 'text-xs'">Tracking not available</p>
            <button @click="isTableVisible = !isTableVisible" :class="isLarge ? 'text-xs at1024:text-sm': 'text-xs'" class="font-semibold hover:text-red" aria-expanded="false">
                <span class="underline underline-offset-4">View <span class="sr-only at480:not-sr-only">package</span> contents</span>
                <BaseSvg :name="isTableVisible ? 'ExpandLess' : 'ExpandMore'" aria-hidden="true" class="size-5" />
            </button>
        </div>
        <div v-if="isTableVisible">
            <!-- NOTE: below is a static/dummy version of the table that's expected to be here.
            I simply don't know how to implement the TableNew.vue component, so for now I've
            mocked up the expected columns/rows/etc. Once the TableNew.vue component is wired
            up, this will need to come back to me (Marc A.) for CSS polish. -->
            <div class="overflow-shadows-horizontal">
                <TableNew
                    class="w-full"
                    :rows="order.lines?.length ? order.lines : order.lines.data"
                    :columns="columns"
                    :row-component="TableRowTwo"
                    :floating-table="true"
                />
            </div>
        </div>
        <p
            :class="hasInvoices ? 'border-green' : 'border-blue'"
            class="bg-gray-125 border-l-[10px] flex items-center p-3 rounded-[10px] at1200:p-4 at1350:grow"
        >
            <span aria-hidden="true" class="leading-none text-sm">
                <b>{{ getMessage.text }}</b>
            </span>
            <span class="border-l border-gray-250 leading-tight ml-4 pl-4 text-xs">{{ getMessage.message }}</span>
        </p>
        <p class="flex gap-1.5 items-center leading-tight" :class="isLarge ? 'text-xs at1024:text-sm': 'text-xs'">
            <BaseSvg name="SeclockIcon" aria-hidden="true" class="bg-white rounded-full shrink-0 size-[14px]" />
            <span>
                {{ getMessage.fullMessage }}
                <a :href="computedDirections.link" class="font-semibold underline hover:text-red">{{ computedDirections.text }}</a>
            </span>
        </p>
    </div>

    <!-- Sourcing Mode -->
    <div v-else>
        <div class="flex gap-2">
            <p class="text-sm">Tracking not available</p>
            <button @click="isTableVisible = !isTableVisible" class="font-semibold text-sm underline decoration-dotted underline-offset-4 hover:decoration-solid" aria-expanded="false">
                <span class="underline underline-offset-4">View <span class="sr-only at480:not-sr-only">package</span> contents</span>
                <BaseSvg :name="isTableVisible ? 'ExpandLess' : 'ExpandMore'" aria-hidden="true" class="size-5" />
            </button>
        </div>
        <div v-if="isTableVisible">
            <!-- NOTE: below is a static/dummy version of the table that's expected to be here.
            I simply don't know how to implement the TableNew.vue component, so for now I've
            mocked up the expected columns/rows/etc. Once the TableNew.vue component is wired
            up, this will need to come back to me (Marc A.) for CSS polish. -->
            <div class="overflow-shadows-horizontal">
                <TableNew
                    class="w-full"
                    :rows="order.lines?.length ? order.lines : order.lines.data"
                    :columns="columns"
                    :row-component="TableRowTwo"
                    :floating-table="true"
                />
            </div>
        </div>
        <p
            :class="hasInvoices ? 'border-green' : 'border-blue'"
            class="bg-gray-125 border-l-[10px] flex items-center p-3 rounded-[10px] at1200:p-4 at1350:grow"
        >
            <span aria-hidden="true" class="leading-none text-sm">
                <b>{{ getMessage.text }}</b>
            </span>
            <span class="border-l border-keyline-primary leading-tight ml-4 pl-4 text-xs">{{ getMessage.message }}</span>
        </p>
        <p class="flex gap-1.5 items-center leading-tight text-sm">
            <BaseSvg name="SeclockIcon" aria-hidden="true" class="rounded-full shrink-0 size-[14px]" />
            <span>
                {{ getMessage.fullMessage }}
                <a :href="computedDirections.link" class="font-semibold underline decoration-dotted underline-offset-4 hover:decoration-solid">{{ computedDirections.text }}</a>
            </span>
        </p>
    </div>
</template>

<script setup>

import {computed, markRaw, ref} from "vue";
import {MoneyFormat, TableNew, TableProductImage, TableRowTwo, TableShorthand} from "@/Components";
import {useHelpers, useUser} from "../../../../../../Composables";
import config from "@/config";
const props = defineProps(['items', 'order', 'cards', 'isLarge']);

const isTableVisible = ref(false);
const { pluralize } = useHelpers();
const { settings } = useUser();
const hasInvoices = computed(() => {
    let invoices = props.order.invoices;

    if(invoices.data) {
        return invoices.data.length > 0;
    }
    return invoices.length > 0;
});

const columns = ref([
    {
        name: "Item #",
        component: markRaw(TableProductImage),
        tdClasses: "w-[400px]",
        props: (row) => ({
            value: props.cards?.[row.item_id],
            link: {
                name: "web.catalog.products.show",
            },
        }),
    },
    {
        name: "Manufacturer",
        component: markRaw(TableShorthand),
        props: (row) => ({
            value: props.cards?.[row.item_id]?.filter_manufacturer,
            classes: "min-w-[120px] max-w-[120px]",
        }),
    },
    {
        name: "Qty",
        path: "quantity_ordered",
        tdClasses: "min-w-20",
    },
    {
        name: "Price",
        component: markRaw(MoneyFormat),
        tdClasses: "min-w-20",
        props: row => ({
            amount: row?.price,
        })
    },
]);

const computedDirections = computed(() => {
    let location = config.locations.find(location => location.code === '01');

    switch (props.order.ship_via_code.toLowerCase()) {
        case "pkup11":
            location = config.locations.find(location => location.code === '11');
            break;
        case "pkup22":
            location = config.locations.find(location => location.code === '22');
            break;
        case "pkup33":
            location = config.locations.find(location => location.code === '33');
            break;
    }
    return {
        link: location.directions,
        text: `${location.city}, ${location.state}`,
    };
});

const getMessage = computed(() => {
    const itemCount = props.order.lines?.length ?? props.order.lines?.data?.length ?? 0;
    const itemText = pluralize(itemCount, 'item', 'items');

    if(hasInvoices.value) {
        return {
            text: 'Order Complete',
            message: 'Your order has been completed. Thank you for your purchase!',
            fullMessage: `Package (${itemCount} ${itemText}) has been picked up from our location in`,
        }
    }
    return {
        text: 'Ready to Pick Up',
        message: 'Your package is ready for pickup at our location.',
        fullMessage: `Package (${itemCount} ${itemText}) ready for pickup at our location in`,
    }
});
</script>
